import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { apiJson } from "../../lib/api";
import { CodexTerminalFrame } from "../ui/CodexTerminalFrame";
import { EmptyState } from "../ui/EmptyState";

type RuntimeState = "observing" | "acting" | "blocked" | "complete";
type QueueStatus = "pending" | "in_progress" | "done" | "blocked";

interface AutopilotTask {
  id: string;
  prompt: string;
  status: QueueStatus;
  created_at: string;
  updated_at: string;
}

interface AutopilotRuntime {
  runtime_id: string;
  state: RuntimeState;
  scope: string;
  started_at: string;
  updated_at: string;
  current_task_id?: string | null;
  last_event?: string | null;
}

interface AutopilotStatusPayload {
  running: boolean;
  runtime: AutopilotRuntime | null;
  summary: {
    pending: number;
    in_progress: number;
    done: number;
    blocked: number;
    total: number;
  };
  current_task: AutopilotTask | null;
  next_task: AutopilotTask | null;
}

interface QueuePayload {
  running: boolean;
  runtime_id: string | null;
  tasks: AutopilotTask[];
  count: number;
}

interface Checkpoint {
  id: string;
  task_id: string;
  title: string;
  kind: string;
  created_at: string;
  status: string;
}

interface CheckpointsPayload {
  running: boolean;
  runtime_id: string | null;
  checkpoints: Checkpoint[];
  count: number;
}

interface EventItem {
  id: string;
  type: string;
  ts: string;
  title: string;
  detail?: Record<string, unknown>;
  source?: string;
}

interface EventsPayload {
  running: boolean;
  runtime_id: string | null;
  events: EventItem[];
  count: number;
}

interface AutopilotRequestResponse {
  ok: boolean;
  accepted: boolean;
  error?: string;
}

const STATE_ACCENT: Record<RuntimeState, string> = {
  observing: "#4f8fff",
  acting: "#7dd3fc",
  blocked: "#f59e0b",
  complete: "#34d399",
};

const STATUS_ACCENT: Record<QueueStatus, string> = {
  pending: "#8b8baa",
  in_progress: "#7dd3fc",
  done: "#34d399",
  blocked: "#f59e0b",
};

function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function AutopilotWorkbench() {
  const [status, setStatus] = useState<AutopilotStatusPayload | null>(null);
  const [queue, setQueue] = useState<QueuePayload | null>(null);
  const [checkpoints, setCheckpoints] = useState<CheckpointsPayload | null>(null);
  const [events, setEvents] = useState<EventsPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("note: define the first local autopilot intake contract");
  const [scope, setScope] = useState("local /autopilot control plane");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [statusData, queueData, checkpointData, eventsData] = await Promise.all([
        apiJson<AutopilotStatusPayload>("/api/autopilot/status"),
        apiJson<QueuePayload>("/api/autopilot/queue"),
        apiJson<CheckpointsPayload>("/api/autopilot/checkpoints"),
        apiJson<EventsPayload>("/api/autopilot/events"),
      ]);
      setStatus(statusData);
      setQueue(queueData);
      setCheckpoints(checkpointData);
      setEvents(eventsData);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = window.setInterval(load, 4000);
    return () => window.clearInterval(interval);
  }, [load]);

  const submitRequest = useCallback(async () => {
    setSubmitting(true);
    setSubmitError(null);
    setSubmitMessage(null);
    try {
      const result = await apiJson<AutopilotRequestResponse>("/api/autopilot/request", {
        method: "POST",
        body: JSON.stringify({
          prompt: draft,
          scope,
        }),
      });
      if (!result.ok || !result.accepted) {
        throw new Error(result.error || "Autopilot did not accept the request.");
      }
      setSubmitMessage("Request accepted by Autopilot.");
      await load();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Failed to submit request.");
    } finally {
      setSubmitting(false);
    }
  }, [draft, load, scope]);

  const runtime = status?.runtime ?? null;
  const accent = runtime ? STATE_ACCENT[runtime.state] : "#4f8fff";
  const queueTasks = queue?.tasks ?? [];
  const recentEvents = useMemo(() => (events?.events ?? []).slice(0, 8), [events]);
  const recentCheckpoints = useMemo(() => (checkpoints?.checkpoints ?? []).slice(0, 6), [checkpoints]);

  if (loading) {
    return (
      <div style={styles.root}>
        <div style={styles.loading}>Loading autopilot runtime…</div>
      </div>
    );
  }

  if (!status?.running || !runtime) {
    return (
      <div style={styles.root}>
        <div style={styles.header}>
          <div>
            <div style={styles.kicker}>Mission Control / Autopilot</div>
            <h1 style={styles.title}>Autopilot control plane</h1>
            <p style={styles.subtitle}>
              No runtime was detected yet. The first accepted request will materialize the local
              runtime and start the queue.
            </p>
          </div>
        </div>
        <section style={styles.panel}>
          <div style={styles.panelLabel}>Request Intake</div>
          <RequestComposer
            draft={draft}
            scope={scope}
            submitting={submitting}
            submitError={submitError}
            submitMessage={submitMessage}
            onDraftChange={setDraft}
            onScopeChange={setScope}
            onSubmit={submitRequest}
          />
        </section>
        <div style={{ marginTop: 18 }}>
          <EmptyState
            icon="M12 2a10 10 0 100 20 10 10 0 000-20z M12 8v4l3 3"
            title="Autopilot is waiting for intake"
            subtitle="Submit a request here and the local runtime will be created automatically."
          />
        </div>
      </div>
    );
  }

  return (
    <div style={styles.root}>
      <div style={styles.header}>
        <div>
          <div style={styles.kicker}>Mission Control / Autopilot</div>
          <h1 style={styles.title}>Observe the loop without interrupting it.</h1>
          <p style={styles.subtitle}>
            This surface is for truthful runtime inspection: state, queue, checkpoints, and derived
            audit evidence. It should answer what the runtime is doing without requiring chat to
            reconstruct it.
          </p>
        </div>
        <div style={{ ...styles.stateBadge, borderColor: `${accent}55`, color: accent }}>
          <span style={{ ...styles.statePulse, background: accent }} />
          {runtime.state}
        </div>
      </div>

      <div style={styles.grid}>
        <section style={styles.panel}>
          <div style={styles.panelLabel}>Request Intake</div>
          <RequestComposer
            draft={draft}
            scope={scope}
            submitting={submitting}
            submitError={submitError}
            submitMessage={submitMessage}
            onDraftChange={setDraft}
            onScopeChange={setScope}
            onSubmit={submitRequest}
          />
        </section>

        <section style={styles.panel}>
          <div style={styles.panelLabel}>Runtime</div>
          <CodexTerminalFrame title="autopilot" prompt={`runtime ${runtime.runtime_id}`}>
            <div style={styles.terminalRows}>
              <Row label="State" value={runtime.state} accent={accent} />
              <Row label="Scope" value={runtime.scope} />
              <Row label="Started" value={fmtDate(runtime.started_at)} />
              <Row label="Updated" value={fmtDate(runtime.updated_at)} />
              <Row label="Last event" value={runtime.last_event || "—"} />
            </div>
          </CodexTerminalFrame>
        </section>

        <section style={styles.panel}>
          <div style={styles.panelLabel}>Summary</div>
          <div style={styles.metricGrid}>
            <Metric label="Total" value={status.summary.total} />
            <Metric label="Pending" value={status.summary.pending} />
            <Metric label="Active" value={status.summary.in_progress} />
            <Metric label="Done" value={status.summary.done} />
          </div>
          <div style={styles.taskStrip}>
            <TaskCard title="Current task" task={status.current_task} />
            <TaskCard title="Next task" task={status.next_task} />
          </div>
        </section>

        <section style={styles.panel}>
          <div style={styles.panelLabel}>Queue</div>
          <div style={styles.list}>
            {queueTasks.length === 0 ? (
              <span style={styles.muted}>No queued work.</span>
            ) : (
              queueTasks.map((task) => (
                <div key={task.id} style={styles.listItem}>
                  <div>
                    <div style={styles.itemTitle}>{task.prompt}</div>
                    <div style={styles.itemMeta}>{task.id} · {fmtDate(task.updated_at)}</div>
                  </div>
                  <span
                    style={{
                      ...styles.statusChip,
                      color: STATUS_ACCENT[task.status],
                      borderColor: `${STATUS_ACCENT[task.status]}55`,
                    }}
                  >
                    {task.status}
                  </span>
                </div>
              ))
            )}
          </div>
        </section>

        <section style={styles.panel}>
          <div style={styles.panelLabel}>Checkpoints</div>
          <div style={styles.list}>
            {recentCheckpoints.length === 0 ? (
              <span style={styles.muted}>No checkpoints emitted yet.</span>
            ) : (
              recentCheckpoints.map((checkpoint) => (
                <div key={checkpoint.id} style={styles.listItem}>
                  <div>
                    <div style={styles.itemTitle}>{checkpoint.title}</div>
                    <div style={styles.itemMeta}>{checkpoint.kind} · {fmtDate(checkpoint.created_at)}</div>
                  </div>
                  <span style={{ ...styles.statusChip, color: "#34d399", borderColor: "#34d39955" }}>
                    {checkpoint.status}
                  </span>
                </div>
              ))
            )}
          </div>
        </section>

        <section style={{ ...styles.panel, gridColumn: "1 / -1" }}>
          <div style={styles.panelLabel}>Audit / Derived Events</div>
          <div style={styles.timeline}>
            {recentEvents.length === 0 ? (
              <span style={styles.muted}>No runtime events available.</span>
            ) : (
              recentEvents.map((event) => (
                <div key={event.id} style={styles.timelineItem}>
                  <div style={styles.timelineDot} />
                  <div>
                    <div style={styles.itemTitle}>{event.title}</div>
                    <div style={styles.itemMeta}>
                      {event.type} · {fmtDate(event.ts)} · {event.source || "runtime"}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function Row({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div style={styles.terminalRow}>
      <span style={styles.terminalLabel}>{label}</span>
      <span style={{ ...styles.terminalValue, color: accent || "var(--ot-text-primary, #f5f7fb)" }}>
        {value}
      </span>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div style={styles.metric}>
      <span style={styles.metricLabel}>{label}</span>
      <strong style={styles.metricValue}>{value}</strong>
    </div>
  );
}

function TaskCard({ title, task }: { title: string; task: AutopilotTask | null }) {
  return (
    <div style={styles.taskCard}>
      <div style={styles.taskCardLabel}>{title}</div>
      {task ? (
        <>
          <div style={styles.itemTitle}>{task.prompt}</div>
          <div style={styles.itemMeta}>{task.id} · {fmtDate(task.updated_at)}</div>
        </>
      ) : (
        <div style={styles.muted}>—</div>
      )}
    </div>
  );
}

function RequestComposer({
  draft,
  scope,
  submitting,
  submitError,
  submitMessage,
  onDraftChange,
  onScopeChange,
  onSubmit,
}: {
  draft: string;
  scope: string;
  submitting: boolean;
  submitError: string | null;
  submitMessage: string | null;
  onDraftChange: (value: string) => void;
  onScopeChange: (value: string) => void;
  onSubmit: () => void;
}) {
  return (
    <div style={styles.composer}>
      <label style={styles.inputLabel}>
        Scope
        <input
          value={scope}
          onChange={(event) => onScopeChange(event.target.value)}
          style={styles.input}
          placeholder="local /autopilot control plane"
        />
      </label>
      <label style={styles.inputLabel}>
        Request
        <textarea
          value={draft}
          onChange={(event) => onDraftChange(event.target.value)}
          style={styles.textarea}
          placeholder="note: describe the bounded work autopilot should accept"
        />
      </label>
      <div style={styles.composerFooter}>
        <button
          type="button"
          onClick={onSubmit}
          disabled={submitting}
          style={{
            ...styles.submitButton,
            opacity: submitting ? 0.7 : 1,
            cursor: submitting ? "progress" : "pointer",
          }}
        >
          {submitting ? "Accepting…" : "Send To Autopilot"}
        </button>
        {submitError ? <span style={styles.errorText}>{submitError}</span> : null}
        {!submitError && submitMessage ? <span style={styles.successText}>{submitMessage}</span> : null}
      </div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  root: {
    padding: 24,
    minHeight: "100%",
    background:
      "linear-gradient(160deg, rgba(4,8,16,0.96) 0%, rgba(8,16,28,0.98) 38%, rgba(4,8,14,1) 100%)",
    color: "var(--ot-text-primary, #f5f7fb)",
  },
  loading: {
    color: "var(--ot-text-muted, #8b8baa)",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 13,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    gap: 20,
    alignItems: "flex-start",
    marginBottom: 24,
  },
  kicker: {
    fontSize: 11,
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    color: "#7dd3fc",
    fontFamily: "'JetBrains Mono', monospace",
    marginBottom: 10,
  },
  title: {
    margin: 0,
    fontSize: 32,
    lineHeight: 1.05,
    letterSpacing: "-0.04em",
    fontFamily: "'JetBrains Mono', monospace",
  },
  subtitle: {
    marginTop: 12,
    maxWidth: 760,
    color: "var(--ot-text-secondary, #9ca3af)",
    lineHeight: 1.7,
    fontSize: 15,
  },
  stateBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    border: "1px solid",
    borderRadius: 999,
    padding: "10px 14px",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    background: "rgba(255,255,255,0.03)",
    flexShrink: 0,
  },
  statePulse: {
    width: 8,
    height: 8,
    borderRadius: 999,
    boxShadow: "0 0 14px currentColor",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 18,
  },
  panel: {
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 22,
    padding: 18,
    background: "rgba(9, 13, 21, 0.9)",
    boxShadow: "0 24px 60px rgba(0,0,0,0.24)",
    backdropFilter: "blur(18px)",
  },
  composer: {
    display: "grid",
    gap: 12,
  },
  inputLabel: {
    display: "grid",
    gap: 8,
    fontSize: 11,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "var(--ot-text-muted, #8b8baa)",
    fontFamily: "'JetBrains Mono', monospace",
  },
  input: {
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.08)",
    background: "rgba(255,255,255,0.03)",
    color: "var(--ot-text-primary, #f5f7fb)",
    padding: "12px 14px",
    fontSize: 14,
    fontFamily: "'JetBrains Mono', monospace",
    outline: "none",
  },
  textarea: {
    minHeight: 112,
    resize: "vertical",
    borderRadius: 16,
    border: "1px solid rgba(255,255,255,0.08)",
    background: "rgba(255,255,255,0.03)",
    color: "var(--ot-text-primary, #f5f7fb)",
    padding: "14px 16px",
    fontSize: 14,
    lineHeight: 1.6,
    fontFamily: "'JetBrains Mono', monospace",
    outline: "none",
  },
  composerFooter: {
    display: "flex",
    gap: 14,
    alignItems: "center",
    flexWrap: "wrap",
  },
  submitButton: {
    borderRadius: 999,
    border: "1px solid rgba(125, 211, 252, 0.35)",
    background: "linear-gradient(135deg, rgba(79,143,255,0.24), rgba(125,211,252,0.18))",
    color: "#d8f4ff",
    padding: "11px 16px",
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.14em",
    fontFamily: "'JetBrains Mono', monospace",
  },
  errorText: {
    color: "#fca5a5",
    fontSize: 12,
    fontFamily: "'JetBrains Mono', monospace",
  },
  successText: {
    color: "#86efac",
    fontSize: 12,
    fontFamily: "'JetBrains Mono', monospace",
  },
  panelLabel: {
    fontSize: 11,
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    color: "var(--ot-text-muted, #8b8baa)",
    fontFamily: "'JetBrains Mono', monospace",
    marginBottom: 14,
  },
  terminalRows: {
    display: "grid",
    gap: 10,
  },
  terminalRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 12,
  },
  terminalLabel: {
    color: "var(--ot-text-muted, #8b8baa)",
  },
  terminalValue: {
    textAlign: "right",
  },
  metricGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 12,
    marginBottom: 18,
  },
  metric: {
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 16,
    padding: 14,
    background: "rgba(255,255,255,0.02)",
  },
  metricLabel: {
    display: "block",
    fontSize: 11,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "var(--ot-text-muted, #8b8baa)",
    fontFamily: "'JetBrains Mono', monospace",
    marginBottom: 10,
  },
  metricValue: {
    fontSize: 28,
    lineHeight: 1,
    fontFamily: "'JetBrains Mono', monospace",
  },
  taskStrip: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
  },
  taskCard: {
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 16,
    padding: 14,
    background: "rgba(255,255,255,0.02)",
  },
  taskCardLabel: {
    fontSize: 11,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "var(--ot-text-muted, #8b8baa)",
    fontFamily: "'JetBrains Mono', monospace",
    marginBottom: 10,
  },
  list: {
    display: "grid",
    gap: 10,
  },
  listItem: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    alignItems: "flex-start",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 14,
    padding: 14,
    background: "rgba(255,255,255,0.02)",
  },
  itemTitle: {
    fontSize: 14,
    lineHeight: 1.5,
    color: "var(--ot-text-primary, #f5f7fb)",
  },
  itemMeta: {
    marginTop: 4,
    fontSize: 11,
    lineHeight: 1.6,
    color: "var(--ot-text-muted, #8b8baa)",
    fontFamily: "'JetBrains Mono', monospace",
  },
  statusChip: {
    border: "1px solid",
    borderRadius: 999,
    padding: "6px 10px",
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    fontFamily: "'JetBrains Mono', monospace",
    flexShrink: 0,
  },
  timeline: {
    display: "grid",
    gap: 12,
  },
  timelineItem: {
    display: "grid",
    gridTemplateColumns: "14px 1fr",
    gap: 12,
    alignItems: "flex-start",
  },
  timelineDot: {
    width: 8,
    height: 8,
    borderRadius: 999,
    marginTop: 8,
    background: "#4f8fff",
    boxShadow: "0 0 16px rgba(79,143,255,0.45)",
  },
  muted: {
    color: "var(--ot-text-muted, #8b8baa)",
    fontSize: 13,
    lineHeight: 1.7,
  },
};
